/**
 * Phase 1 — Upload + Triage.
 *
 * Werkstatt-Einstieg: User lädt PDF hoch, Karte wird angelegt,
 * URL aktualisiert mit ?card=<id>, dann wartet Phase 1 auf Triage-Abschluss.
 *
 * Production code path:
 *  - POST /api/timesheets/upload (file, month, client_id)
 *  - Hub: speichert Datei + DB-Row, enqueued OCR-Job
 *  - Worker conflict.ts: prüft employee+month-Duplikate
 *  - State-Pfad: pending → pending_review | ocr_complete | pending_conflict
 *
 * Output dieser Phase = Input von Phase 2:
 *   - card{id, kind, state, pdf_*}
 *   - state muss NICHT mehr `pending` sein damit Phase 2 beginnt
 */
import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import type { WerkstattCardContext } from "../../pages/WerkstattPage";
import { PipelineStepList, type PipelineStep } from "../SettingsTestOcr";
import { useClientContext } from "../../lib/ClientContext";
import { api } from "../../lib/api";
import { stepUuid } from "../../lib/stepUuid";
import { RotationAutoTest } from "./RotationAutoTest";
import { ImagePreprocessLab } from "./ImagePreprocessLab";

/** Attaches a deterministic UUID to each step. Same card+phase+num → same UUID. */
export function withUuids(cardId: string | null, phase: string, steps: PipelineStep[]): PipelineStep[] {
  if (!cardId) return steps;
  return steps.map(s => ({ ...s, uuid: stepUuid(cardId, phase, s.num) }));
}

interface Phase1Props {
  ctx: WerkstattCardContext;
  setCardId: (id: string) => void;
}

export function Phase1Upload({ ctx, setCardId }: Phase1Props) {
  const c = ctx.card;
  return (
    <div>
      <PhaseHeader
        num={1}
        title="Upload + Triage"
        purpose="PDF entgegennehmen, klassifizieren, in OCR-Queue stellen"
        inputs="PDF-Datei + client_id + month"
        outputs="card{id, kind, state, pdf_*} + ggf. OCR-Job in pg-boss"
        prodFiles={[
          "hub/src/routes/timesheets.ts (POST /upload)",
        ]}
        userGates="(1) PDF + Monat wählen  (2) Upload starten  (3) bei kind=unknown: manuell klassifizieren"
      />

      {!c ? (
        <>
          <UploadForm onUploaded={setCardId} />
          <OrSeparator />
          <ExistingCardSelector
            cards={ctx.allCards}
            employees={ctx.employees}
            onPick={setCardId}
          />
        </>
      ) : (
        <UploadedCardSteps ctx={ctx} />
      )}
    </div>
  );
}

function OrSeparator() {
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 12,
      margin: "16px 0", color: "#9ca3af", fontSize: 12, fontWeight: 600,
    }}>
      <div style={{ flex: 1, height: 1, background: "#e5e7eb" }} />
      <span style={{ letterSpacing: 1 }}>ODER</span>
      <div style={{ flex: 1, height: 1, background: "#e5e7eb" }} />
    </div>
  );
}

// ─── Upload-Form (Werkstatt-Einstieg) ─────────────────────────────────

function UploadForm({ onUploaded }: { onUploaded: (id: string) => void }) {
  const { selectedClientId } = useClientContext();
  const [file, setFile] = useState<File | null>(null);
  const [month, setMonth] = useState<string>(new Date().toISOString().slice(0, 7));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!file || !month) return;
    setErr(null);
    setBusy(true);
    try {
      const ts = await api.timesheets.upload(month, file, selectedClientId);
      // In manual-mode (user pref process_mode='manual') state stays "uploaded"
      // until /start-ocr is called. Werkstatt always wants the next phase to
      // proceed → auto-trigger OCR. In autonomous-mode this is a no-op (state
      // already ocr_pending) and returns 409 which we swallow.
      if (ts.state === "uploaded") {
        try { await api.timesheets.startOcr(ts.id); } catch { /* already running */ }
      }
      onUploaded(ts.id);
    } catch (e: any) {
      setErr(e.message ?? String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="phase1-upload-form" style={{
      padding: 16, marginBottom: 14,
      background: "#f8fafc", border: "1px dashed #94a3b8", borderRadius: 6,
    }}>
      <strong>Neue Karte hochladen</strong>
      <div style={{ display: "flex", gap: 16, alignItems: "end", marginTop: 12, flexWrap: "wrap" }}>
        <label className="field">
          <span>Monat</span>
          <input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            required
          />
        </label>
        <label className="field" style={{ flex: 1, minWidth: 280 }}>
          <span>PDF-Datei</span>
          <input
            type="file"
            accept="application/pdf"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            required
          />
        </label>
        <button type="submit" disabled={!file || !month || busy}>
          {busy ? "Lade hoch…" : "Hochladen + Triage starten"}
        </button>
      </div>
      <p className="muted" style={{ margin: "8px 0 0 0", fontSize: 12 }}>
        Mandant: <code>{selectedClientId ?? "(keiner gewählt)"}</code> · max 10 MB ·
        Nach Upload wird die Karten-ID in die URL geschrieben und Phase 1 zeigt den Live-Fortschritt.
      </p>
      {err && <div style={{ color: "#dc2626", marginTop: 8 }}>Fehler: {err}</div>}
    </form>
  );
}

// ─── Status der hochgeladenen Karte ───────────────────────────────────

function UploadedCardSteps({ ctx }: { ctx: WerkstattCardContext }) {
  const c = ctx.card!;
  const loc = useLocation();
  const [starting, setStarting] = useState(false);

  // pending_conflict zählt für Phase 1 als "durch" — der Konflikt entsteht erst
  // in Phase 2 vom Worker und wird dort/in Phase 7 aufgelöst. Phase 1 = nur Upload+Triage.
  // archived/superseded sind Terminal-States: Pipeline beendet, NICHT mehr "wartend".
  const POST_TRIAGE = ["ocr_pending", "ocr_running", "ocr_complete", "validated",
                       "calculated", "exported", "pending_conflict",
                       "pending_review",  // Triage IS durch — pending_review ist
                                          // ein nachgelagerter Manual-Orientation-
                                          // oder Disambig-State, nicht Triage-Fail.
                       "archived", "superseded"];
  const TERMINATED = ["archived", "superseded"];
  const triageDone = POST_TRIAGE.includes(c.state);
  const stuckUploaded = c.state === "uploaded";
  const terminated = TERMINATED.includes(c.state);
  const triageOk = triageDone;

  async function startOcrNow() {
    setStarting(true);
    try {
      await api.timesheets.startOcr(c.id);
      ctx.reload();
    } finally {
      setStarting(false);
    }
  }

  // Kind-aware Source-Pfade. Stundenliste-zentrierte Texte vorher gaben für
  // Diäten-Karten falsche Hinweise (Pfad timesheets/, Source-File timesheets.ts).
  const isExpense = c.kind === "expense" || c.kind === "unknown";
  const targetTable = isExpense ? "expenses" : "timesheets";
  const targetDir = isExpense ? "expenses" : "timesheets";
  const createdAtPretty = (() => {
    try { return new Date(c.created_at).toLocaleString("de-AT"); }
    catch { return String(c.created_at); }
  })();
  const needsManualOrientation = c.state === "pending_review"
    && (c.data as any)?.needs_review_reason === "manual_orientation";

  const steps: PipelineStep[] = [
    {
      num: 1,
      title: "PDF-Upload",
      description:
        `Datei nach /app/hub/data/${targetDir}/<id>.pdf, DB-Row in ${targetTable}, `
        + `state=uploaded oder ocr_pending je nach user.process_mode. `
        + `Source: hub/src/routes/uploads.ts → POST /api/uploads/pdf · multipart parse, `
        + `dann classify pro Page (timesheet/expense/unknown) und Persist in passende Tabelle.`,
      status: "done",
      detail: `${c.pdf_orig_filename} · ${formatBytes(c.pdf_bytes)} · Monat ${c.month} · erstellt ${createdAtPretty}`,
    },
    {
      num: 2,
      title: "Dokumenttyp-Routing (manuelle Wahl)",
      description:
        "Der Dokumenttyp wird beim Upload vom User EXPLIZIT gewählt (mandant-spezifisch, "
        + "z.B. Stundenliste→timesheets, Diäten→expenses). Diese Wahl ist autoritativ "
        + "fürs Routing (Ziel-Tabelle + nächster Worker-Job). "
        + "Source: hub/src/lib/docTypes.ts + hub/src/routes/uploads.ts (doc_type). "
        + "Die frühere Auto-Klassifikation (ocr/preprocess.py:_classify) läuft weiter, "
        + "bestimmt aber NICHT mehr das Routing — sie dient nur noch als stille "
        + "Plausibilitäts-Warnung (data.classification_warning), wenn sie der User-Wahl "
        + "widerspricht. Legacy-Fallback ohne doc_type: alter Threshold-Gate-Pfad "
        + "(score<0.70 → unknown → Triage, hub/src/routes/uploads.ts:applyClassificationGate).",
      status: c.kind === "unknown" ? "error"
        : triageDone ? "done" : "pending",
      detail: (() => {
        const score = (c as any).classification_score;
        const scoreStr = typeof score === "number" ? ` (score=${score.toFixed(2)})` : "";
        if (c.kind === "timesheet") return `kind=timesheet${scoreStr} → OCR-Job enqueued (card.ocr)`;
        if (c.kind === "expense") return `kind=expense${scoreStr} → expense-OCR-Pfad (heute paddle_vl Legacy)`;
        if (c.kind === "unknown") {
          const tooLow = typeof score === "number" && score < 0.70;
          return tooLow
            ? `kind=unknown${scoreStr} → unter Threshold 0.70, User disambiguiert in Triage-Inbox`
            : `kind=unknown${scoreStr} → OCR-Engine konnte nicht klassifizieren`;
        }
        return `kind=${String((c as any).kind)}`;
      })(),
    },
    {
      num: 3,
      title: "Image-Preprocessing-Lab (optional)",
      description:
        "Vor dem OCR-Lauf können wir die Original-Vorlage mit Pre-Processing-"
        + "Varianten (CLAHE, Sauvola, Hough-Redraw) bearbeiten und vergleichen "
        + "wie sich die Anchor-Detection-Qualität ändert. Glass-Box-Lab: "
        + "Original bleibt unverändert, jede Variante ist ein eigenes Atom "
        + "(deterministische ID, eigener PNG-File, eigene Measure). Eine Variante "
        + "als active setzen → Worker nutzt PNG der Variante statt PDF-Original. "
        + "Zurück zum Original jederzeit möglich. Anlassfall: Karten mit "
        + "insufficient_anchor_coverage (40db9d36 / 6d76180f / 842c9f99). "
        + "Source: ocr/preprocess_lab.py + hub/src/routes/preprocess.ts.",
      status: "pending",  // optional step, kein status-gate
      detail: (
        <ImagePreprocessLab cardId={c.id} />
      ),
    },
    {
      num: 4,
      title: terminated
        ? "Pipeline beendet"
        : needsManualOrientation
          ? "Manuelle Orientierungsklärung nötig"
          : "Bereit für Phase 2",
      description: terminated
        ? "Karte ist im Terminal-State (archived/superseded). Pipeline hat keine "
          + "weiteren Schritte mehr — Datei wird für Audit aufbewahrt, ist aber inaktiv."
        : needsManualOrientation
          ? "Phase-7-Rotation-Resolver (Tier-1..3) hat keine eindeutige Orientierung "
            + "gefunden — Tier-4 (User) muss entscheiden ob die Seite 0°/90°/180°/270° gedreht "
            + "werden muss bevor OCR sinnvoll laufen kann. Die Karte ist HEUTE in einem "
            + "Sammelbecken, das in der bestehenden UI noch nicht prominent angezeigt wird. "
            + "TODO Production: Rotation-Disambiguation-UI als Phase-7-Variante anbieten."
          : "Phase 2 wird freigeschaltet sobald state in {ocr_pending, ocr_running, ocr_complete, …}. "
            + "Bei manual-mode (state=uploaded) muss zuerst OCR via Button unten gestartet werden. "
            + "Conflict-Detection (employee+month Duplikate) passiert in Phase 2 vom Worker, nicht hier.",
      status: terminated ? "done"
            : needsManualOrientation ? "error"
            : triageOk ? "done"
            : stuckUploaded ? "error"
            : "pending",
      detail: terminated ? (
        <>
          Pipeline beendet · state=<code>{c.state}</code>
          {c.state === "archived" && " (discard)"}
          {c.state === "superseded" && " (overwrite)"}
          {" "}— OCR-Audit unter{" "}
          <Link to={`/werkstatt/abrechnung/ocr?card=${c.id}`}>Phase 2</Link>{" "}einsehbar,
          Konflikt-History in{" "}
          <Link to={`/werkstatt/abrechnung/konflikt?card=${c.id}`}>Phase 7</Link>.
        </>
      ) : needsManualOrientation ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <div>
            ⏸ state=<code>pending_review</code> · Pre-OCR-Tier-4-Resolver wartet auf
            User-Entscheidung. Bis das passiert läuft kein OCR — die Karte hängt
            quasi vor Phase 2 fest.{" "}
            <Link
              to={`/${isExpense ? "expenses" : "timesheets"}/${c.id}`}
              state={{ background: loc }}
              style={{ fontWeight: 600 }}
            >
              🔍 Detail-View öffnen (Rotation klären) →
            </Link>
          </div>
          <div style={{ fontSize: 12, opacity: 0.85 }}>
            Automatisches Gate via Tesseract OSD — direkt auf die Original-PDF.
            Sagt entweder „Seite gerade" oder „Drehung um N° nötig". Bei niedriger
            Confidence (Handschrift, schwacher Druck) bleibt die User-Entscheidung
            der einzig verlässliche Pfad.
          </div>
          <RotationAutoTest cardId={c.id} />
        </div>
      ) : triageOk ? (
        <>state={c.state} ✓ Weiter mit Phase 2</>
      ) : stuckUploaded ? (
        "⏸ state=uploaded — Manual-Mode, OCR via Button starten"
      ) : (
        `Warte… aktueller state=${c.state}`
      ),
    },
  ];

  return (
    <>
      <PipelineStepList steps={withUuids(c.id, "upload", steps)} />
      {stuckUploaded && (
        <div className="werkstatt-actions" style={{ marginTop: 12 }}>
          <button type="button" onClick={startOcrNow} disabled={starting}>
            {starting ? "Starte OCR…" : "OCR jetzt starten (Manual-Mode)"}
          </button>
          <span className="muted" style={{ marginLeft: 12, fontSize: 12 }}>
            User-Preference process_mode=manual — OCR wird nicht automatisch getriggert.
          </span>
        </div>
      )}
    </>
  );
}

// ─── Optional: bestehende Karte laden ─────────────────────────────────

function ExistingCardSelector(props: {
  cards: WerkstattCardContext["allCards"];
  employees: WerkstattCardContext["employees"];
  onPick: (id: string) => void;
}) {
  return (
    <div className="phase1-existing" style={{
      padding: 16,
      background: "#f8fafc",
      border: "1px dashed #94a3b8",
      borderRadius: 6,
    }}>
      <strong>Bestehende Karte inspizieren</strong>
      <p className="muted" style={{ margin: "4px 0 12px 0", fontSize: 12 }}>
        {props.cards.length} Karten der letzten 6 Monate. Auswahl zeigt die Pipeline-
        Steps der gewählten Karte im aktuellen State an.
      </p>
      <select
        defaultValue=""
        onChange={(e) => e.target.value && props.onPick(e.target.value)}
        style={{ minWidth: 420, width: "100%", maxWidth: 600 }}
      >
        <option value="">— Karte wählen —</option>
        {props.cards.map(c => {
          const emp = props.employees.find(e => e.id === c.employee_id);
          const label = emp ? `${emp.last_name}, ${emp.first_name}` : "(unzugeordnet)";
          return (
            <option key={c.id} value={c.id}>
              {c.month} · {label} · {c.state} · {c.id.slice(0, 8)}
            </option>
          );
        })}
      </select>
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────

function formatBytes(n: number | string | null | undefined): string {
  const v = typeof n === "string" ? parseInt(n, 10) : (n ?? 0);
  if (!v) return "0 B";
  if (v < 1024) return `${v} B`;
  if (v < 1024 * 1024) return `${(v / 1024).toFixed(1)} KB`;
  return `${(v / 1024 / 1024).toFixed(2)} MB`;
}

export function PhaseHeader(props: {
  num: number;
  title: string;
  purpose: string;
  inputs: string;
  outputs: string;
  prodFiles: string[];
  userGates: string;
}) {
  return (
    <div className="phase-header">
      <h3 style={{ margin: "0 0 4px 0" }}>
        Phase {props.num} — {props.title}
      </h3>
      <p className="muted" style={{ margin: "0 0 8px 0", fontSize: 13 }}>
        {props.purpose}
      </p>
      <div className="phase-header-grid">
        <div><strong>Input</strong>  {props.inputs}</div>
        <div><strong>Output</strong>  {props.outputs}</div>
        <div><strong>User-Gates</strong>  {props.userGates}</div>
        <div>
          <strong>Production-Code</strong>{" "}
          {props.prodFiles.map(f => (
            <code key={f} style={{ marginRight: 6 }}>{f}</code>
          ))}
        </div>
      </div>
    </div>
  );
}
